/* src/pages/admin/AdminCategoryManager.js (NEW FILE) */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AdminCategoryManager = () => {
    const [summary, setSummary] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for the rename modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [newName, setNewName] = useState('');

    const fetchSummary = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('app_token');
            const response = await axios.get('/api/admin/categories/summary', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSummary(response.data);
        } catch (err) {
            setError('Failed to load category summary.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    const handleOpenModal = (category) => {
        setEditingCategory(category);
        setNewName(category.name); // Pre-fill the input with the current name
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingCategory(null);
        setNewName('');
    };

    const handleRename = async (e) => {
        e.preventDefault();
        if (!editingCategory || !newName.trim() || newName === editingCategory.name) {
            handleCloseModal();
            return;
        }

        try {
            const token = localStorage.getItem('app_token');
            await axios.put('/api/admin/categories/rename', 
                { oldName: editingCategory.name, newName },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            handleCloseModal();
            fetchSummary(); // Refresh the data
        } catch (err) {
            alert('Failed to rename category.');
            console.error(err);
        }
    };

    return (
        <div className="admin-content-manager">
            {/* Rename Modal */}
            {isModalOpen && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-content" style={{ maxWidth: '500px' }}>
                        <h2>Rename/Merge Category</h2>
                        <p>Renaming "<strong>{editingCategory.name}</strong>" will update it across all content. If you enter an existing category name, this will merge them.</p>
                        <form onSubmit={handleRename} className="admin-form">
                            <div className="form-group">
                                <label htmlFor="newName">New Category Name</label>
                                <input
                                    type="text"
                                    id="newName"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="form-actions">
                                <button type="button" onClick={handleCloseModal} className="btn-secondary">Cancel</button>
                                <button type="submit" className="btn-primary">Save Changes</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="admin-content-header">
                <h3 className="admin-section-title">Category Management</h3>
                <button onClick={fetchSummary} className="btn-secondary" disabled={isLoading}>
                    <span className="material-icons" style={{ verticalAlign: 'middle', marginRight: '0.5rem' }}>refresh</span>
                    Refresh
                </button>
            </div>

            <p style={{ margin: '-1rem 0 1.5rem 0', color: '#64748b' }}>
                This tool allows you to rename or merge existing categories. A category is created automatically when you assign it to a new piece of content.
            </p>

            {isLoading ? <p>Loading categories...</p> : error ? <p className="error-message">{error}</p> : (
                <div className="admin-table-container">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Category Name</th>
                                <th>Total Items</th>
                                <th>Articles</th>
                                <th>Videos</th>
                                <th>Podcasts</th>
                                <th>Reels</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {summary.map(cat => (
                                <tr key={cat.name}>
                                    <td><strong>{cat.name}</strong></td>
                                    <td>{cat.total}</td>
                                    <td>{cat.articles}</td>
                                    <td>{cat.videos}</td>
                                    <td>{cat.podcasts}</td>
                                    <td>{cat.reels}</td>
                                    <td className="admin-table-actions">
                                        <button onClick={() => handleOpenModal(cat)} className="action-btn edit" title="Rename/Merge">
                                            <span className="material-icons">drive_file_rename_outline</span>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdminCategoryManager;
